import { useState } from 'react';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import type { AddAccessoryToPrinterInput, UpdateAccessoryInput } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SlideOver } from '@/components/SlideOver';
import { ComboBox } from '@/components/ComboBox';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Spinner } from '@/components/Spinner';
import {
  usePrinterAccessories,
  useAccessoryTypes,
  useAddAccessory,
  useUpdateAccessory,
  useRemoveAccessory,
  useCreateAccessoryType,
} from '../hooks';

interface AccessoryManagerProps {
  printerId: string;
}

// Form data type with string dates (from HTML inputs)
type AccessoryFormData = Omit<Partial<AddAccessoryToPrinterInput>, 'installedDate'> & {
  installedDate?: string;
};

export function AccessoryManager({ printerId }: AccessoryManagerProps) {
  const [isAddingAccessory, setIsAddingAccessory] = useState(false);
  const [editingAccessory, setEditingAccessory] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { data: accessories, isLoading } = usePrinterAccessories(printerId);
  const { data: accessoryTypes, isLoading: typesLoading } = useAccessoryTypes();
  const addAccessory = useAddAccessory();
  const updateAccessory = useUpdateAccessory();
  const removeAccessory = useRemoveAccessory();
  const createType = useCreateAccessoryType();

  const [formData, setFormData] = useState<AccessoryFormData>({
    quantity: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setFormData({ quantity: 1 });
    setErrors({});
    setIsAddingAccessory(false);
    setEditingAccessory(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setIsAddingAccessory(true);
  };

  const handleOpenEdit = (accessoryId: string) => {
    const accessory = accessories?.find((a) => a.id === accessoryId);
    if (accessory) {
      setFormData({
        quantity: accessory.quantity,
        installedDate: accessory.installedDate ? new Date(accessory.installedDate).toISOString().split('T')[0] : undefined,
        notes: accessory.notes || undefined,
      });
      setEditingAccessory(accessoryId);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (isAddingAccessory && !formData.accessoryTypeId) {
      newErrors.accessoryTypeId = 'Accessory type is required';
    }
    if (!formData.quantity || formData.quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (editingAccessory) {
        const updateData: UpdateAccessoryInput = {
          quantity: formData.quantity!,
          installedDate: formData.installedDate ? new Date(formData.installedDate as string) : undefined,
          notes: formData.notes || undefined,
        };
        await updateAccessory.mutateAsync({
          printerId,
          accessoryId: editingAccessory,
          input: updateData,
        });
      } else {
        const addData: AddAccessoryToPrinterInput = {
          accessoryTypeId: formData.accessoryTypeId!,
          quantity: formData.quantity!,
          installedDate: formData.installedDate ? new Date(formData.installedDate as string) : undefined,
          notes: formData.notes || undefined,
        };
        await addAccessory.mutateAsync({
          printerId,
          input: addData,
        });
      }
      resetForm();
    } catch {
      // Error handled by mutation
    }
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await removeAccessory.mutateAsync({ printerId, accessoryId: deleteTarget });
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCreateAccessoryType = async (name: string) => {
    const newType = await createType.mutateAsync({
      name,
      category: 'OTHER',
      addsNozzleSlots: 0,
      addsHotendSlots: 0,
      addsSpoolSlots: 0,
      addsAccessorySlots: 0,
    });
    handleChange('accessoryTypeId', newType.id);
  };

  const accessoryTypeOptions = (accessoryTypes || []).map((type) => ({
    value: type.id,
    label: `${type.name} ${type.category !== 'OTHER' ? `(${type.category})` : ''}`,
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Accessories</h3>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={handleOpenAdd}>
          Add Accessory
        </Button>
      </div>

      {!accessories || accessories.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">No accessories installed yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accessories.map((accessory) => (
            <div
              key={accessory.id}
              className="flex items-start justify-between rounded-lg border border-border bg-background-subtle p-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-foreground">{accessory.accessoryType.name}</h4>
                  {accessory.accessoryType.category !== 'OTHER' && (
                    <span className="rounded bg-background px-2 py-0.5 text-xs text-foreground-muted">
                      {accessory.accessoryType.category}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-1 text-sm text-foreground-muted">
                  <div>Quantity: {accessory.quantity}</div>
                  {accessory.installedDate && (
                    <div>
                      Installed: {new Date(accessory.installedDate).toLocaleDateString()}
                    </div>
                  )}
                  {accessory.notes && <div className="mt-2 text-xs">{accessory.notes}</div>}
                  {accessory.accessoryType.description && (
                    <div className="mt-2 text-xs italic">{accessory.accessoryType.description}</div>
                  )}
                  {/* Show slot modifications */}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {accessory.accessoryType.addsNozzleSlots > 0 && (
                      <span className="rounded bg-background px-2 py-0.5">
                        +{accessory.accessoryType.addsNozzleSlots * accessory.quantity} nozzle slots
                      </span>
                    )}
                    {accessory.accessoryType.addsHotendSlots > 0 && (
                      <span className="rounded bg-background px-2 py-0.5">
                        +{accessory.accessoryType.addsHotendSlots * accessory.quantity} hotend slots
                      </span>
                    )}
                    {accessory.accessoryType.addsSpoolSlots > 0 && (
                      <span className="rounded bg-background px-2 py-0.5">
                        +{accessory.accessoryType.addsSpoolSlots * accessory.quantity} spool slots
                      </span>
                    )}
                    {accessory.accessoryType.addsAccessorySlots > 0 && (
                      <span className="rounded bg-background px-2 py-0.5">
                        +{accessory.accessoryType.addsAccessorySlots * accessory.quantity} accessory slots
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenEdit(accessory.id)}
                  title="Edit accessory"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteTarget(accessory.id)}
                  title="Remove accessory"
                >
                  <Trash2 className="h-4 w-4 text-error" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      <SlideOver
        open={isAddingAccessory || !!editingAccessory}
        onClose={resetForm}
        title={editingAccessory ? 'Edit Accessory' : 'Add Accessory'}
      >
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <div className="flex-1 space-y-6 overflow-y-auto p-4">
            <FormSection title="Accessory Details" defaultOpen>
              <div className="space-y-4">
                {isAddingAccessory && (
                  <ComboBox
                    label="Accessory Type"
                    value={formData.accessoryTypeId || ''}
                    onChange={(value) => handleChange('accessoryTypeId', value)}
                    onCreateNew={handleCreateAccessoryType}
                    error={errors.accessoryTypeId}
                    disabled={typesLoading}
                    options={accessoryTypeOptions}
                    placeholder="Select or create accessory type..."
                    createLabel="Create accessory type"
                    required
                  />
                )}
                <Input
                  label="Quantity"
                  type="number"
                  min={1}
                  value={formData.quantity || 1}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value))}
                  error={errors.quantity}
                  required
                />
                <Input
                  label="Installed Date"
                  type="date"
                  value={formData.installedDate || ''}
                  onChange={(e) => handleChange('installedDate', e.target.value || undefined)}
                />
                <Textarea
                  label="Notes"
                  placeholder="Add notes about this accessory..."
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value || undefined)}
                  rows={3}
                />
              </div>
            </FormSection>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
            <Button variant="ghost" onClick={resetForm} disabled={addAccessory.isPending || updateAccessory.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={addAccessory.isPending || updateAccessory.isPending}>
              {editingAccessory ? 'Save Changes' : 'Add Accessory'}
            </Button>
          </div>
        </form>
      </SlideOver>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleRemove}
        title="Remove Accessory"
        description="Are you sure you want to remove this accessory? This will update the printer's slot counts."
        confirmText="Remove"
        variant="danger"
        loading={removeAccessory.isPending}
      />
    </div>
  );
}
